import { defaultMemoize } from 'reselect';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { UserIdentityConflictDefinition } from '~src/authentication/contentDefinitions/UserIdentityConflictDefinition';
import { CollectionDefinition } from '~src/Collections/contentDefinitions/CollectionDefinition';
import { ConnectedApplicationDefinition } from '~src/connectedApplications/content/ConnectedApplicationDefinition';
import { MetricDefinition } from '~src/metrics/models/MetricDefinition';
import { ProjectDefinition } from '~src/projects/components/contentDefinitions/ProjectDefinition';
import { RecommendedViewDefinition } from '~src/recommendations/contentDefinitions/RecommendedViewDefinition';
import { isSimulationEnabled } from '~src/simulation/util/SimulationUtils';
import { setCollectionSpaceContentInOrder, setContentSpaceDefinitions,setExplorableItemDefinitions, setFavoriteMixedContentInOrder, setMixedContentInOrder, setPersonalSpaceContentInOrder } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemDefinition, MixedContentItemDefinition, SpaceDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsTypes';
import {
  ExplorableItemType,
  SpaceInfoItemType,
} from '~src/spacesPlatform/models/ContentType';
import { ViewDefinition } from '~src/views/contentDefinitions/ViewDefinition';

import { BackgroundJobDefinition } from '../../backgroundJob/components/contentDefinitions/BackgroundJobDefinition';
import { DatabaseDefinition, DatabaseWithCrassDefinition } from '../../catalog/components/contentDefinitions/DatabaseDefinition';
import { TableDefinition, TableWithCrassDefinition } from '../../catalog/components/contentDefinitions/TableDefinition';
import { AccelerationTaskDefinition } from '../../dataAcceleration/contentDefinitions/AccelerationTaskDefinition';
import { DataRoleDefinition } from '../../dataRole/contentDefinitions/DataRoleDefinition';
import { ConnectedWorkbooksInDatasourceDefinition } from '../../datasource/components/contentDefinitions/ConnectedWorkbooksInDatasourceDefinition';
import { DatasourceDefinition } from '../../datasource/components/contentDefinitions/DatasourceDefinition';
import { DataConnectionDefinition } from '../../datasource/dataConnection/components/contentDefinitions/DataConnectionDefinition';
import { DataConnectionInDatasourceDefinition } from '../../datasource/dataConnection/components/contentDefinitions/DataConnectionInDatasourceDefinition';
import { DataAlertDefinition } from '../../dda/contentDefinitions/DataAlertDefinition';
import { RemoteRefreshSchedulesDefinition } from '../../extractRefresh/contentDefinitions/RemoteRefreshSchedulesDefinition';
import { ExtractTaskDefinition } from '../../extractTask/components/spaces/contentDefinitions/ExtractTaskDefinition';
import { FlowDefinition } from '../../flow/contentDefinitions/FlowDefinition';
import { FlowOutputStepRunDefinition } from '../../flow/contentDefinitions/FlowOutputStepRunDefinition';
import { RunFlowTaskDefinition } from '../../flow/contentDefinitions/RunFlowTaskDefinition';
import { LensDefinition } from '../../lens/contentDefinitions/LensDefinition';
import { PredictiveModelDefinition } from '../../predictiveModel/contentDefinition/PredictiveModelDefinition';
import { PublishedConnectionColumnDefinition } from '../../publishedConnection/contentDefinitions/PublishedConnectionColumnDefinition';
import { PublishedConnectionDefinition } from '../../publishedConnection/contentDefinitions/PublishedConnectionDefinition';
import { PublishedConnectionTableDefinition } from '../../publishedConnectionTable/contentDefinitions/PublishedConnectionTableDefinition';
import { ResourceGovernanceSettingDefiniton } from '../../schedule/components/contentDefinitions/ResourceGovernanceSettingDefinition';
import { ScheduleDefinition } from '../../schedule/components/contentDefinitions/ScheduleDefinition';
import { SimulationDefinition } from '../../simulation/contentDefinitions/SimulationDefinition';
import { SiteDefinition } from '../../siteManagement/contentDefinitions/SiteDefinition';
import { SubscriptionDefinition } from '../../subscription/contentDefinitions/SubscriptionDefinition';
import { GroupDefinition } from '../../userManagement/groups/components/contentDefinitions/GroupDefinition';
import { ServerUserDefinition } from '../../userManagement/users/contentDefinition/ServerUserDefinition';
import { SiteUserDefinition } from '../../userManagement/users/contentDefinition/SiteUserDefinition';
import { CustomViewDefinition } from '../../views/contentDefinitions/customViews/CustomViewDefinition';
import { WorkbookDefinition } from '../../workbook/components/contentDefinitions/WorkbookDefinition';
import { SearchUserDefinition } from './SearchUserDefinition';

const mixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  MetricDefinition,
  LensDefinition,
  DatasourceDefinition,
  FlowDefinition,
  DataRoleDefinition,
  PublishedConnectionDefinition,
  PredictiveModelDefinition,
];

const collectionSpaceContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  MetricDefinition,
  LensDefinition,
  DatasourceDefinition,
  FlowDefinition,
  DataRoleDefinition,
  PublishedConnectionDefinition,
  PublishedConnectionTableDefinition,
  PredictiveModelDefinition,
];

const personalSpaceContentInOrder: MixedContentItemDefinition[] = [
  WorkbookDefinition,
];

const favoriteMixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  MetricDefinition,
  DatasourceDefinition,
  FlowDefinition,
  DataRoleDefinition,
  CollectionDefinition,
  PublishedConnectionDefinition,
  PublishedConnectionTableDefinition
];

const explorableItemDefinitions: Record<
  ExplorableItemType,
  ExplorableItemDefinition
> = {
  accelerationTask: AccelerationTaskDefinition,
  backgroundJob: BackgroundJobDefinition,
  collection: CollectionDefinition,
  connectedApplication: ConnectedApplicationDefinition,
  connectedWorkbook: ConnectedWorkbooksInDatasourceDefinition,
  connection: DataConnectionInDatasourceDefinition,
  customView: CustomViewDefinition,
  dataalert: DataAlertDefinition,
  database: DatabaseDefinition,
  databaseWithCrass: DatabaseWithCrassDefinition,
  dataconnection: DataConnectionDefinition,
  dataRole: DataRoleDefinition,
  datasource: DatasourceDefinition,
  extractTask: ExtractTaskDefinition,
  flow: FlowDefinition,
  flowOutputStepRun: FlowOutputStepRunDefinition,
  group: GroupDefinition,
  lens: LensDefinition,
  metric: MetricDefinition,
  predictiveModel: PredictiveModelDefinition,
  project: ProjectDefinition,
  virtualConnection: PublishedConnectionDefinition,
  virtualConnectionTable: PublishedConnectionTableDefinition,
  virtualConnectionColumn: PublishedConnectionColumnDefinition,
  recommendedView: RecommendedViewDefinition,
  remoteRefreshSchedule: RemoteRefreshSchedulesDefinition,
  resourceGovernanceSetting: ResourceGovernanceSettingDefiniton,
  runFlowTask: RunFlowTaskDefinition,
  schedule: ScheduleDefinition,
  searchuser: SearchUserDefinition,
  serveruser: ServerUserDefinition,
  userIdentityConflict: UserIdentityConflictDefinition,
  site: SiteDefinition,
  siteuser: SiteUserDefinition,
  subscription: SubscriptionDefinition,
  table: TableDefinition,
  tableWithCrass: TableWithCrassDefinition,
  view: ViewDefinition,
  workbook: WorkbookDefinition,
  simulation: SimulationDefinition,
};

const contentSpaceDefinitions: {
  [C in SpaceInfoItemType]: SpaceDefinition<C>;
} = {
  collection: CollectionDefinition,
  connectedApplication: ConnectedApplicationDefinition,
  database: DatabaseDefinition,
  databaseWithCrass: DatabaseWithCrassDefinition,
  dataRole: DataRoleDefinition,
  datasource: DatasourceDefinition,
  flow: FlowDefinition,
  group: GroupDefinition,
  lens: LensDefinition,
  metric: MetricDefinition,
  predictiveModel: PredictiveModelDefinition,
  project: ProjectDefinition,
  virtualConnection: PublishedConnectionDefinition,
  virtualConnectionTable: PublishedConnectionTableDefinition,
  resourceGovernanceSetting: ResourceGovernanceSettingDefiniton,
  schedule: ScheduleDefinition,
  serveruser: ServerUserDefinition,
  siteuser: SiteUserDefinition,
  view: ViewDefinition,
  workbook: WorkbookDefinition,
  simulation: SimulationDefinition,
  table: TableDefinition,
  tableWithCrass: TableWithCrassDefinition
};

// NOTE: FF values can be overriden at runtime, and they need to be passed as parameters
// so that the evaluations for content definition lists can occur at runtime (DEFECT 1412349).
// Memoization is also needed here in order to prevent unnecessary calls when these
// content definition lists are used as dependencies of other memoized methods.
// For example, `FilterByTypePanel` has such method which refetches content type counts
// whenever dependencies change. Without memoization, new lists would be returned here
// even when FF values don't change, leading to unnecessary calls to fetch counts.
const mixedContentInOrderWithFFGatedContentDefinition = (isDatabaseEnabled: boolean, isSimulationEnabled: boolean) => [
  ...mixedContentInOrder,
  ...isDatabaseEnabled ? [DatabaseWithCrassDefinition] : [],
  ...isSimulationEnabled ? [SimulationDefinition] : [],
];
const memoizedMixedContentInOrderWithFFGatedContentDefinition = defaultMemoize(mixedContentInOrderWithFFGatedContentDefinition);
setMixedContentInOrder(undefined, () => memoizedMixedContentInOrderWithFFGatedContentDefinition(
  FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled,
  isSimulationEnabled()
));

const collectionSpaceContentInOrderWithFFGatedContentDefinition = (isSimulationEnabled: boolean) => [
  ...collectionSpaceContentInOrder,
  ...isSimulationEnabled ? [SimulationDefinition] : [],
];
const memoizedCollectionSpaceContentInOrderWithFFGatedContentDefinition = defaultMemoize(collectionSpaceContentInOrderWithFFGatedContentDefinition);
setCollectionSpaceContentInOrder(undefined, () => memoizedCollectionSpaceContentInOrderWithFFGatedContentDefinition(
  isSimulationEnabled()
));

const personalSpaceContentInOrderWithFFGatedContentDefinition = (isSimulationEnabled: boolean) => [
  ...personalSpaceContentInOrder,
  ...isSimulationEnabled ? [SimulationDefinition] : [],
];
const memoizedPersonalSpaceContentInOrderWithFFGatedContentDefinition = defaultMemoize(personalSpaceContentInOrderWithFFGatedContentDefinition);
setPersonalSpaceContentInOrder(undefined, () => memoizedPersonalSpaceContentInOrderWithFFGatedContentDefinition(
  isSimulationEnabled()
));

const favoriteSpaceContentInOrderWithFFGatedContentDefinition = (isLensEnabled: boolean, isSimulationEnabled: boolean) => [
  ...favoriteMixedContentInOrder,
  ...isLensEnabled ? [LensDefinition] : [],
  ...isSimulationEnabled ? [SimulationDefinition] : [],
];
const memoizedFavoriteSpaceContentInOrderWithFFGatedContentDefinition = defaultMemoize(favoriteSpaceContentInOrderWithFFGatedContentDefinition);
setFavoriteMixedContentInOrder(undefined, () => memoizedFavoriteSpaceContentInOrderWithFFGatedContentDefinition(
  FeatureFlags.askDataLensFavoritingEnabled,
  isSimulationEnabled()
));

setExplorableItemDefinitions(explorableItemDefinitions);
setContentSpaceDefinitions(contentSpaceDefinitions);
